import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useI18n } from "../../../i18n"
import type { TranslationKey } from "../../../i18n/types"
import type { PanelProps } from "../types"
import {
  isRePausing,
  scheduleWho,
  type DoorRun,
  type ScheduleListView,
  type ScheduleRow,
  type ScheduleRun,
} from "../../../shared/schedules"
import {
  DEFAULT_CADENCE,
  buildSchedule,
  currentTzOffsetMinutes,
  type CadenceState,
} from "../cadence"
import { CadencePicker } from "../CadencePicker"
import {
  ScheduleApiError,
  actOnSchedule,
  cancelSchedule,
  createSchedule,
  editSchedule,
  fetchScheduleRuns,
  fetchSchedules,
  setKillSwitch,
} from "../schedules-api"
import { takePendingScheduleOrigin } from "../useSettingsSurface"

// C7 (HF-6). Three kinds of row in one list, visually distinct: task (an agent
// instruction Hermes runs), reminder (a push MUSE fires itself), cadence (a
// wake — present, deliberately not creatable until HF-5 gives it hands).
//
// Every state this panel renders is the gateway's own honest state. A kill
// switch that could not confirm every job paused says PARTIAL in red; it never
// says "all paused".

const POLL_INTERVAL_MS = 15_000

const MAX_TASK_PROMPT = 5000
const MAX_REMINDER_BODY = 1000

type FormKind = "task" | "reminder" | "cadence"

interface FormState {
  open: boolean
  editingId: string | null
  kind: FormKind
  identity: "rook" | "rainer"
  name: string
  prompt: string
  cadence: CadenceState
  /** Session-bound wakes, slice 2′ — set only by the "Wake me on this"
   *  prefill; `null` for every ordinary create and for every edit (editing
   *  never re-binds — `origin_session_id` is not updatable). */
  originSessionId: string | null
  originSessionLabel: string | null
}

const EMPTY_FORM: FormState = {
  open: false,
  editingId: null,
  kind: "task",
  identity: "rainer",
  name: "",
  prompt: "",
  cadence: DEFAULT_CADENCE,
  originSessionId: null,
  originSessionLabel: null,
}

const KIND_LABEL: Record<FormKind, TranslationKey> = {
  task: "settings.schedules.kind.task",
  reminder: "settings.schedules.kind.reminder",
  cadence: "settings.schedules.kind.cadence",
}

/** The unit suffixes are translated; "in" reads the same in both locales. */
type Translate = (key: TranslationKey, vars?: Record<string, string | number>) => string

/** "in 9h" / "in 4m" / "now" — coarse on purpose, this is a glance surface. */
function relativeTo(iso: string | null, now: number, t: Translate): string | null {
  if (!iso) return null
  const delta = Date.parse(iso) - now
  if (Number.isNaN(delta)) return null
  if (delta <= 0) return t("settings.schedules.unit.now")
  const minutes = Math.round(delta / 60_000)
  if (minutes < 60) return `in ${minutes}${t("settings.schedules.unit.m")}`
  const hours = Math.round(minutes / 60)
  if (hours < 48) return `in ${hours}${t("settings.schedules.unit.h")}`
  return `in ${Math.round(hours / 24)}${t("settings.schedules.unit.d")}`
}

function ago(iso: string | null, now: number, t: Translate): string | null {
  if (!iso) return null
  const delta = now - Date.parse(iso)
  if (Number.isNaN(delta) || delta < 0) return null
  const seconds = Math.round(delta / 1000)
  if (seconds < 60) return `${seconds}${t("settings.schedules.unit.s")}`
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}${t("settings.schedules.unit.m")}`
  return `${Math.round(minutes / 60)}${t("settings.schedules.unit.h")}`
}

/** ADR-024: the newest door-ledger line for a row, or null (it has not run, or only deferred). */
function lastDoorRun(view: ScheduleListView, scheduleId: string): DoorRun | null {
  return (view.door_runs ?? []).find((r) => r.schedule_id === scheduleId) ?? null
}

export function SchedulesPanel(_props: PanelProps) {
  const { t } = useI18n()
  const [view, setView] = useState<ScheduleListView | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [runs, setRuns] = useState<ScheduleRun[]>([])
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const formRef = useRef<HTMLFormElement>(null)
  const radiogroupRef = useRef<HTMLDivElement>(null)
  // Whatever opened the form (New schedule, or a row's Edit) — focus goes back
  // there on every close path. Same discipline as SettingsSurface's openerRef.
  const formOpenerRef = useRef<HTMLElement | null>(null)
  const newScheduleRef = useRef<HTMLButtonElement>(null)
  const formWasOpenRef = useRef(false)
  const tzOffset = useMemo(() => currentTzOffsetMinutes(), [])
  const now = Date.now()

  const load = useCallback(async (fresh: boolean) => {
    try {
      setView(await fetchSchedules(fresh))
      setError(null)
    } catch (err) {
      setError(err instanceof ScheduleApiError ? err.message : "Could not load schedules.")
    }
  }, [])

  useEffect(() => {
    void load(true)
    const timer = setInterval(() => void load(false), POLL_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [load])

  // Session-bound wakes, slice 2′ — "Wake me on this" leaves a one-shot
  // prefill for the panel to pick up on mount. Read-and-clear by construction
  // (takePendingScheduleOrigin), so a later remount never inherits it again.
  useEffect(() => {
    const pending = takePendingScheduleOrigin()
    if (!pending) return
    formOpenerRef.current = document.activeElement as HTMLElement | null
    setForm({
      ...EMPTY_FORM,
      open: true,
      kind: "task",
      originSessionId: pending.sessionId,
      originSessionLabel: pending.label,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Focus moves into the form when it opens and back to the opener when it
  // closes. The selector includes [role="radio"][aria-checked="true"] because
  // the kind picker is <button role="radio">, not an <input> — it is first in
  // the form, so the checked kind is what receives focus.
  useEffect(() => {
    if (form.open && !formWasOpenRef.current) {
      formRef.current
        ?.querySelector<HTMLElement>('[role="radio"][aria-checked="true"], input, select, textarea')
        ?.focus()
    } else if (!form.open && formWasOpenRef.current) {
      // The New-schedule button is unmounted while the form is open, so the
      // captured opener can be a detached node by now; its fresh instance is
      // the right landing spot. A row's Edit button never leaves the DOM.
      const opener = formOpenerRef.current
      ;(opener?.isConnected ? opener : newScheduleRef.current)?.focus()
      formOpenerRef.current = null
    }
    formWasOpenRef.current = form.open
  }, [form.open])

  // role="radio" roving tabindex — ported from ModelsPanel.tsx so the two
  // radiogroups in Settings behave identically under the keyboard.
  function handleRadiogroupKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return
    const items = Array.from(radiogroupRef.current?.querySelectorAll<HTMLElement>('[role="radio"]') ?? [])
    if (items.length === 0) return
    e.preventDefault()
    const activeIndex = items.indexOf(document.activeElement as HTMLElement)
    const delta = e.key === "ArrowDown" ? 1 : -1
    const next = activeIndex === -1 ? 0 : (activeIndex + delta + items.length) % items.length
    items[next].focus()
  }

  const built = useMemo(() => buildSchedule(form.cadence, tzOffset), [form.cadence, tzOffset])

  async function guard(action: () => Promise<unknown>) {
    setBusy(true)
    try {
      await action()
      await load(true)
      setError(null)
    } catch (err) {
      setError(err instanceof ScheduleApiError ? err.message : "Something went wrong.")
    } finally {
      setBusy(false)
    }
  }

  async function toggleExpand(row: ScheduleRow) {
    if (expandedId === row.id) {
      setExpandedId(null)
      return
    }
    setExpandedId(row.id)
    setRuns([])
    try {
      setRuns(await fetchScheduleRuns(row.id, 10))
    } catch {
      // A failed run history is not worth an error banner — the row still shows.
    }
  }

  function closeForm() {
    setForm(EMPTY_FORM)
  }

  function openCreate() {
    formOpenerRef.current = document.activeElement as HTMLElement | null
    setForm({ ...EMPTY_FORM, open: true })
  }

  function openEdit(row: ScheduleRow) {
    formOpenerRef.current = document.activeElement as HTMLElement | null
    setForm({
      open: true,
      editingId: row.id,
      kind: row.kind,
      identity: row.identity,
      name: row.name,
      prompt: row.prompt ?? "",
      // Editing always re-picks the schedule from scratch: the stored cron is
      // UTC and the offset it was built with is not recoverable.
      cadence: { ...DEFAULT_CADENCE, mode: "advanced", raw: row.schedule },
      // Editing never re-binds a wake's origin — origin_session_id is not
      // updatable (schedules-store.ts UPDATABLE_COLUMNS), same rule as
      // identity. A bound row's origin is fixed at creation, full stop.
      originSessionId: null,
      originSessionLabel: null,
    })
  }

  async function submitForm(e: React.FormEvent) {
    e.preventDefault()
    if (!built.schedule) return
    await guard(async () => {
      if (form.editingId) {
        await editSchedule(form.editingId, {
          name: form.name,
          prompt: form.prompt,
          schedule: built.schedule,
          schedule_display: built.display,
        })
      } else {
        await createSchedule({
          identity: form.kind === "reminder" ? "rainer" : form.identity,
          kind: form.kind,
          name: form.name,
          prompt: form.prompt,
          schedule: built.schedule,
          schedule_display: built.display,
          // Omit the key entirely when unbound — the gateway treats an
          // absent/null origin_session_id as "unbound" but rejects a
          // malformed one, so `undefined` (dropped by JSON.stringify) is the
          // only safe way to say "no binding".
          origin_session_id: form.originSessionId ?? undefined,
        })
      }
      closeForm()
    })
  }

  const kill = view?.kill_switch
  const killDetail = kill?.engage_detail ?? []
  const killFailed = killDetail.filter((d) => !d.ok).length
  const killStatusText = !kill?.on
    ? t("settings.schedules.killOff")
    : kill.engage_status === "ok"
      ? t("settings.schedules.killOn", { total: killDetail.length })
      : kill.engage_status === "partial"
        ? t("settings.schedules.killPartial", { failed: killFailed, total: killDetail.length })
        : kill.engage_status === "failed"
          ? t("settings.schedules.killFailed")
          : t("settings.schedules.killPending")
  const killIsAlarming = kill?.on === true && kill.engage_status !== "ok"

  const promptMax = form.kind === "reminder" ? MAX_REMINDER_BODY : MAX_TASK_PROMPT

  return (
    <>
      {/* ── 1. Status strip ─────────────────────────────────────────────── */}
      <div className="settings-section">
        <div className="settings-section-label">{t("settings.schedules.killSwitch")}</div>
        <div className="settings-row">
          <div>
            <div
              className={killIsAlarming ? "settings-row-label settings-schedule-alarm" : "settings-row-label"}
              role="status"
            >
              {killStatusText}
            </div>
            <div className="settings-row-hint">{t("settings.schedules.killSwitchHint")}</div>
          </div>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={busy}
            onClick={() => void guard(() => setKillSwitch(!kill?.on))}
          >
            {kill?.on ? t("settings.schedules.release") : t("settings.schedules.engage")}
          </button>
        </div>

        {view &&
          (Object.keys(view.hermes) as Array<"rook" | "rainer">).map((identity) => {
            const info = view.hermes[identity]!
            return (
              <div className="settings-row" key={identity}>
                <div className="settings-row-label">{identity === "rook" ? "Rook" : "Rainer"}</div>
                <div className="settings-row-value">
                  {info.reachable ? t("settings.schedules.hermesReachable") : t("settings.schedules.hermesUnreachable")}
                  {info.reachable && info.model_label ? ` · ${info.model_label}` : ""}
                </div>
              </div>
            )
          })}

        {view?.door &&
          (Object.keys(view.door.companions) as Array<"rook" | "rainer">).map((identity) => {
            const door = view.door!.companions[identity]
            return (
              <div className="settings-row" key={`door-${identity}`}>
                <div className="settings-row-label">{identity === "rook" ? "Rook" : "Rainer"}</div>
                <div className="settings-row-value" data-testid={`door-line-${identity}`}>
                  {door.enabled
                    ? t("settings.schedules.doorOn", {
                        used: door.used_this_week,
                        limit: door.weekly_limit,
                        minutes: door.max_minutes,
                      })
                    : t("settings.schedules.doorOff")}
                  {door.enabled && door.holding ? ` · ${t("settings.schedules.doorHolding", { why: door.holding })}` : ""}
                </div>
              </div>
            )
          })}

        <div className="settings-row-hint">
          {view && ago(view.reconciled_at, now, t)
            ? t("settings.schedules.reconciled", { age: ago(view.reconciled_at, now, t)! })
            : t("settings.schedules.reconciledNever")}
        </div>

        {error && (
          <div role="alert" className="settings-schedule-alarm settings-row-hint">
            {error}
          </div>
        )}
      </div>

      {/* ── 2. List ─────────────────────────────────────────────────────── */}
      <div className="settings-section">
        <div className="settings-section-label">{t("settings.schedules.listLabel")}</div>

        {view && view.schedules.length === 0 && (
          <div className="settings-row-hint">{t("settings.schedules.empty")}</div>
        )}

        {view?.schedules.map((row) => {
          const next = relativeTo(row.next_run_at, now, t)
          const expanded = expandedId === row.id
          return (
            <div key={row.id} className={`settings-schedule-row settings-schedule-row--${row.kind}`}>
              <div className="settings-schedule-main">
                <button
                  type="button"
                  className="settings-schedule-expand"
                  aria-expanded={expanded}
                  aria-label={`${t("settings.schedules.expand")}: ${row.name}`}
                  onClick={() => void toggleExpand(row)}
                >
                  <span className={`settings-schedule-badge settings-schedule-badge--${row.kind}`}>
                    {t(KIND_LABEL[row.kind])}
                  </span>
                  {row.created_by === "companion" && (
                    <span className="settings-schedule-badge">{t("settings.schedules.companionCreated")}</span>
                  )}
                  {row.engine === "claude" && (
                    <span className="settings-schedule-badge" data-testid="runs-as-claude">
                      {t("settings.schedules.runsAsClaude")}
                    </span>
                  )}
                  <span className="settings-schedule-name">{row.name}</span>
                  <span className="settings-schedule-who">{scheduleWho(row)}</span>
                  <span className="settings-schedule-when">{row.schedule_display || row.schedule}</span>
                  <span className="settings-schedule-next">{next ? t("settings.schedules.next", { when: next }) : t("settings.schedules.nextNone")}</span>
                  <span className={`settings-schedule-state settings-schedule-state--${row.desired_state}`}>
                    {t(`settings.schedules.state.${row.desired_state}` as TranslationKey)}
                  </span>
                </button>

                <div className="settings-schedule-actions">
                  {row.sync_status !== "unmanaged" && row.kind !== "cadence" && (
                    <button
                      type="button"
                      className="btn btn-ghost"
                      disabled={busy}
                      aria-label={`${t("settings.schedules.runNow")}: ${row.name}`}
                      onClick={() => {
                        // ADR-024: a Claude wake spends Falco's own allowance, so Run now shows the
                        // stored note and asks first. A Hermes row runs as it always did.
                        if (
                          row.engine === "claude" &&
                          !window.confirm(
                            t("settings.schedules.runNowPreview", { who: scheduleWho(row), prompt: row.prompt ?? row.name }),
                          )
                        ) {
                          return
                        }
                        void guard(() => actOnSchedule(row.id, "run"))
                      }}
                    >
                      {t("settings.schedules.runNow")}
                    </button>
                  )}
                  {/* Arm is hidden on an unmanaged row: the gateway refuses it
                      with 400 read_only (spec §4.2 — pause and cancel only). */}
                  {(row.desired_state === "armed" || row.sync_status !== "unmanaged") && (
                    <button
                      type="button"
                      className="btn btn-ghost"
                      disabled={busy}
                      aria-label={`${
                        row.desired_state === "armed" ? t("settings.schedules.pause") : t("settings.schedules.arm")
                      }: ${row.name}`}
                      onClick={() =>
                        void guard(() => actOnSchedule(row.id, row.desired_state === "armed" ? "pause" : "arm"))
                      }
                    >
                      {row.desired_state === "armed" ? t("settings.schedules.pause") : t("settings.schedules.arm")}
                    </button>
                  )}
                  {row.sync_status !== "unmanaged" && (
                    <button
                      type="button"
                      className="btn btn-ghost"
                      disabled={busy}
                      aria-label={`${t("settings.schedules.edit")}: ${row.name}`}
                      onClick={() => openEdit(row)}
                    >
                      {t("settings.schedules.edit")}
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={busy}
                    aria-label={`${
                      row.sync_status === "orphaned" ? t("settings.schedules.remove") : t("settings.schedules.cancel")
                    }: ${row.name}`}
                    onClick={() => void guard(() => cancelSchedule(row.id))}
                  >
                    {row.sync_status === "orphaned" ? t("settings.schedules.remove") : t("settings.schedules.cancel")}
                  </button>
                </div>
              </div>

              {row.engine === "claude" && view && (() => {
                const last = lastDoorRun(view, row.id)
                if (!last) return null
                const outcome = t(`settings.schedules.door.${last.status}` as TranslationKey)
                return (
                  <div className="settings-row-hint" data-testid="door-last-run">
                    {last.duration_ms === null
                      ? t("settings.schedules.doorLastRunNoTime", { outcome })
                      : t("settings.schedules.doorLastRun", {
                          outcome,
                          minutes: Math.max(1, Math.round(last.duration_ms / 60_000)),
                        })}
                  </div>
                )
              })()}
              {isRePausing(row) && (
                <div className="settings-row-hint">{t("settings.schedules.rePausing")}</div>
              )}
              {row.sync_status === "completed" && (
                <div className="settings-row-hint">
                  {t("settings.schedules.sync.completed", { age: ago(row.last_run_at, now, t) ?? t("settings.schedules.unit.now") })}
                  {row.last_error && <span className="settings-schedule-chip"> · {row.last_error}</span>}
                </div>
              )}
              {(row.sync_status === "orphaned" || row.sync_status === "unreachable" || row.sync_status === "unmanaged") && (
                <div className="settings-schedule-chip">
                  {t(`settings.schedules.sync.${row.sync_status}` as TranslationKey)}
                </div>
              )}

              {expanded && (
                <div className="settings-schedule-detail">
                  <p className="settings-row-hint">{row.prompt ?? "—"}</p>
                  <p className="settings-model-facts">
                    {row.kind === "reminder" || row.engine === "claude"
                      ? ""
                      : view.hermes[row.identity]?.model_label
                        ? t("settings.schedules.modelFact", { model: view.hermes[row.identity]!.model_label! })
                        : t("settings.schedules.modelFactUnknown")}
                  </p>
                  <div className="settings-section-label">{t("settings.schedules.runsHeading")}</div>
                  {runs.length === 0 && <div className="settings-row-hint">{t("settings.schedules.noRuns")}</div>}
                  {runs.map((run) => (
                    <div className="settings-row-hint" key={run.id}>
                      {run.fired_at} · {run.status ?? "—"}
                      {run.error ? ` · ${run.error}` : ""}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* ── 3. Create / edit ────────────────────────────────────────────── */}
      <div className="settings-section">
        {!form.open && (
          <button type="button" className="btn btn-ghost" ref={newScheduleRef} onClick={openCreate}>
            {t("settings.schedules.newSchedule")}
          </button>
        )}

        {form.open && (
          <form
            ref={formRef}
            className="settings-schedule-form"
            onSubmit={(e) => void submitForm(e)}
            onKeyDown={(e) => {
              if (e.key !== "Escape") return
              // Without this the surface's own document-level Escape listener
              // fires too and closes all of Settings, discarding what was typed.
              e.stopPropagation()
              closeForm()
            }}
          >
            <div
              role="radiogroup"
              aria-label={t("settings.schedules.newSchedule")}
              className="settings-schedule-kinds"
              ref={radiogroupRef}
              onKeyDown={handleRadiogroupKeyDown}
            >
              {(["task", "reminder", "cadence"] as const).map((kind) => (
                <button
                  key={kind}
                  type="button"
                  role="radio"
                  aria-checked={form.kind === kind}
                  tabIndex={form.kind === kind ? 0 : -1}
                  disabled={kind === "cadence"}
                  className="settings-model-row"
                  onClick={() => setForm({ ...form, kind })}
                >
                  <span className="settings-model-radio" aria-hidden="true" />
                  <span>
                    <span className="settings-model-label">{t(KIND_LABEL[kind])}</span>
                    {kind === "cadence" && (
                      <span className="settings-model-facts">{t("settings.schedules.cadenceDisabled")}</span>
                    )}
                  </span>
                </button>
              ))}
            </div>

            {form.originSessionId && (
              <div className="settings-row-hint" role="note">
                {form.originSessionLabel
                  ? t("settings.schedules.fromThisChat", { label: form.originSessionLabel })
                  : t("settings.schedules.fromThisChatUntitled")}
              </div>
            )}

            {form.kind !== "reminder" && (
              <>
                <label className="settings-row-label" htmlFor="schedule-who">
                  {t("settings.schedules.who")}
                </label>
                <select
                  id="schedule-who"
                  className="settings-select"
                  value={form.identity}
                  onChange={(e) => setForm({ ...form, identity: e.target.value as "rook" | "rainer" })}
                >
                  <option value="rainer">Rainer</option>
                  <option value="rook">Rook</option>
                </select>
              </>
            )}

            <label className="settings-row-label" htmlFor="schedule-name">
              {t("settings.schedules.name")}
            </label>
            <input
              id="schedule-name"
              className="settings-inline-input"
              type="text"
              maxLength={200}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />

            <CadencePicker
              value={form.cadence}
              onChange={(cadence) => setForm({ ...form, cadence })}
              built={built}
            />

            <label className="settings-row-label" htmlFor="schedule-prompt">
              {form.kind === "reminder" ? t("settings.schedules.message") : t("settings.schedules.prompt")}
            </label>
            <textarea
              id="schedule-prompt"
              className="settings-inline-input"
              rows={3}
              maxLength={promptMax}
              value={form.prompt}
              onChange={(e) => setForm({ ...form, prompt: e.target.value })}
            />
            <div className="settings-row-hint">
              {t("settings.schedules.charsLeft", { n: promptMax - form.prompt.length })}
            </div>

            <div className="settings-row-hint">{t("settings.schedules.createdPausedHint")}</div>

            <div className="settings-schedule-actions">
              <button type="submit" className="btn btn-ghost" disabled={busy || !built.schedule}>
                {form.editingId ? t("settings.schedules.save") : t("settings.schedules.create")}
              </button>
              <button type="button" className="btn btn-ghost" onClick={closeForm}>
                {t("settings.schedules.close")}
              </button>
            </div>
          </form>
        )}
      </div>
    </>
  )
}
